import React, { useEffect, useMemo, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import {
  Autocomplete,
  Button,
  Chip,
  IconButton,
  Popover,
  Stack,
  TextField,
  TextFieldProps,
  Typography
} from "@mui/material";
import { useTaskManageActions } from "store";
import { useTaskDealsSelect } from "store";
import { DealBase } from "store/types";
import { IContactsQueries } from "store/types/user-management";

const commonInputProps: {
  size: TextFieldProps["size"];
  InputLabelProps: TextFieldProps["InputLabelProps"];
} = {
  size: "small",
  InputLabelProps: { shrink: true }
};

interface IFiltersProps {
  queries: IContactsQueries;
  onChange: (partial: Partial<IContactsQueries>) => void;
}

const chipSx = {
  height: "24px",
  borderRadius: "32px",
  backgroundColor: "#f6f8fc",
  color: "#071f35",
  fontSize: "12px",
  ".MuiChip-deleteIcon": { fontSize: "16px", color: "#071f35", "&:hover": { color: "#071f35" } }
} as const;

// Contacts filter bar — mirrors the Users "Filters" button + popover, and shows applied filters as
// removable chips. Filters: Deal (→ deal_id, supported by the list endpoint) and Organization (firm).
export const Filters: React.FC<IFiltersProps> = ({ queries, onChange }) => {
  const { getDealsAction } = useTaskManageActions();
  const deals = useTaskDealsSelect();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [draftDeal, setDraftDeal] = useState<DealBase | null>(null);
  const [draftFirm, setDraftFirm] = useState("");

  // Load the account's deals once for the Deal dropdown (same source as the Task-manage filter).
  useEffect(() => {
    if (!deals.length) getDealsAction();
  }, [deals.length, getDealsAction]);

  const selectedDeal = useMemo(
    () => deals.find((deal) => deal.id === queries.deal_id) ?? null,
    [deals, queries.deal_id]
  );
  const hasFilters = Boolean(queries.deal_id || queries.firm);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    // Seed the popover draft from the currently applied filters.
    setDraftDeal(selectedDeal);
    setDraftFirm(queries.firm ?? "");
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleApply = () => {
    onChange({ deal_id: draftDeal?.id, firm: draftFirm.trim() || undefined });
    handleClose();
  };

  return (
    <Stack direction="row" sx={{ alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
      <Typography sx={{ fontSize: "15px", color: "#071f35" }}>Filter:</Typography>
      <IconButton
        onClick={handleOpen}
        aria-label="add filter"
        sx={{
          color: "#071f35",
          width: "24px",
          height: "24px",
          backgroundColor: "#F6F8FC",
          fontSize: "18px",
          fontWeight: 300,
          lineHeight: 1,
          "&:hover": { backgroundColor: "#e9eef5" }
        }}
      >
        +
      </IconButton>

      {selectedDeal && (
        <Chip
          label={`Deal: ${selectedDeal.title}`}
          onDelete={() => onChange({ deal_id: undefined })}
          deleteIcon={<CloseIcon />}
          sx={chipSx}
        />
      )}
      {queries.firm && (
        <Chip
          label={`Organization: ${queries.firm}`}
          onDelete={() => onChange({ firm: undefined })}
          deleteIcon={<CloseIcon />}
          sx={chipSx}
        />
      )}
      {hasFilters && (
        <Typography
          component="button"
          onClick={() => onChange({ deal_id: undefined, firm: undefined })}
          sx={{ fontSize: "12px", color: "#26a6ae", background: "none", border: "none", cursor: "pointer", p: 0 }}
        >
          Clear all
        </Typography>
      )}

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Stack sx={{ p: "36px", gap: "24px", width: "450px" }}>
          <Autocomplete
            options={deals}
            value={draftDeal}
            getOptionLabel={(option) => option.title}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.title}
              </li>
            )}
            onChange={(_, value) => setDraftDeal(value)}
            renderInput={(params) => (
              <TextField {...params} {...commonInputProps} label="Deal" placeholder={draftDeal ? "" : "Select"} />
            )}
          />

          <TextField
            {...commonInputProps}
            label="Organization"
            placeholder="Search by organization"
            value={draftFirm}
            onChange={(event) => setDraftFirm(event.target.value)}
          />

          <Stack direction="row" justifyContent="flex-end" alignItems="center" gap="10px">
            <Button size="sm" variant="outlined" sx={{ minWidth: "53px" }} onClick={handleClose}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant="contained"
              sx={{ backgroundColor: "#071F35", minWidth: "46px" }}
              onClick={handleApply}
            >
              Apply
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </Stack>
  );
};
